"use client";

import { IconCheckOutline18, IconPencilOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import type { MDXEditorMethods } from "@mdxeditor/editor";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useTemplatesStore, type Template } from "@/stores/templatesStore";
import { TemplateDescriptionMarkdown, preserveBlankLines } from "./TemplateDescriptionMarkdown";
const MarkdownWysiwygEditor = dynamic(() => import("./MarkdownWysiwygEditor").then(m => m.MarkdownWysiwygEditor), {
  ssr: false,
  loading: () => <div className="border-ds-neutral-250 bg-ds-surface-card flex min-h-[320px] items-center justify-center rounded-lg border">
      <PixelLoader size="sm" />
    </div>
});
interface EditableTemplateDescriptionProps {
  template: Template;
  canEdit: boolean;
  tokenRefs?: {
    tokenAddress: string;
    chainId: number;
  }[];
}
export function EditableTemplateDescription({
  template,
  canEdit,
  tokenRefs
}: EditableTemplateDescriptionProps) {
  const updateTemplate = useTemplatesStore(state => state.updateTemplate);
  const [description, setDescription] = useState(template.description ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<MDXEditorMethods | null>(null);
  const draftRef = useRef(template.description ?? "");
  useEffect(() => {
    setDescription(template.description ?? "");
  }, [template.description]);
  useEffect(() => {
    if (!isOpen) {
      const nextDraft = description;
      draftRef.current = nextDraft;
      setError(null);
    }
  }, [description, isOpen]);
  const handleDraftChange = useCallback((markdown: string) => {
    draftRef.current = markdown;
  }, []);
  const handleSave = useCallback(async () => {
    const markdown_0 = editorRef.current?.getMarkdown() ?? draftRef.current;
    const next = preserveBlankLines(markdown_0.trim());
    if (next === description.trim()) {
      setIsOpen(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const updatedTemplate = await updateTemplate(template.id, {
        description: next
      });
      setDescription(updatedTemplate.description ?? next);
      draftRef.current = updatedTemplate.description ?? next;
      setIsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [description, template.id, updateTemplate]);
  const hasDescription = Boolean(description.trim());
  if (!hasDescription && !canEdit) return null;
  return <section className="mb-10">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-ds-text-primary text-base font-semibold">About</h2>
        {canEdit && <Button variant="white" size="xs" onClick={() => setIsOpen(true)} className="rounded-full" title="Edit description">
            <IconPencilOutline18 className="h-3 w-3" />
            Edit description
          </Button>}
      </div>
      {hasDescription ? <div className="text-ds-text-tertiary prose-sm">
          <TemplateDescriptionMarkdown content={description} tokenRefs={tokenRefs} />
        </div> : <p className="text-ds-text-disabled text-sm italic">No description yet. Click Edit description to add one.</p>}

      <Dialog open={isOpen} onOpenChange={open => !isSaving ? setIsOpen(open) : undefined}>
        <DialogContent className="max-w-3xl" innerClassName="bg-ds-surface-card rounded-ds-lg relative grid max-h-[85vh] gap-4 overflow-hidden p-0">
          <DialogHeader className="flex-row items-center justify-between border-b border-black/5 px-6 py-4">
            <DialogTitle className="text-content-secondary text-base font-semibold">Edit description</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6">
            <MarkdownWysiwygEditor editorRef={editorRef} initialMarkdown={description} onChange={handleDraftChange} placeholder="Describe what this template does…" />
            {error && <p className="text-2-5xs mt-2 text-red-600">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-black/5 px-6 py-4">
            <Button variant="white" size="sm" onClick={() => setIsOpen(false)} disabled={isSaving}>
              <IconXmarkOutline18 className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button variant="brand" size="sm" onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? <PixelLoader size="xs" /> : <IconCheckOutline18 className="h-3.5 w-3.5" />}
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}